import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useSelectedComponent } from '@/hooks/useSelectedComponent';
import ComponentNotFound from '@/hooks/ComponentNotFound';
import OnboardingPageHeader from '@/components/smartai/ComponentOnboarding/OnboardingPageHeader';
import OnboardingProjectHeader from '@/components/smartai/ComponentOnboarding/OnboardingProjectHeader';
import { registerTestIntelligenceScanSession } from '@/components/smartai/api/registerScanSession';
import TestCasesTab from './TestCasesTab';
import TestCasesSkeleton, { TestCasesTabSkeleton } from './TestCasesSkeleton';
import {
  testCasesCardChildClass,
  testCasesCardClass,
  testCasesEmptyStateClass,
  testCasesPageClass,
} from './test-cases-layout';

const formatBranch = (branch: string | undefined): string =>
  (branch || 'main').replace(/^refs\/heads\//, '');

const TestCasesPage = () => {
  const { selected: component, ready, isLoading, notFound } =
    useSelectedComponent();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const scanId = component.scanId?.trim() ?? '';
  const [sessionReady, setSessionReady] = useState(!scanId);

  useEffect(() => {
    if (!scanId) {
      setSessionReady(true);
      return;
    }

    let cancelled = false;
    setSessionReady(false);
    void registerTestIntelligenceScanSession(scanId)
      .then(() => {
        if (!cancelled) {
          void queryClient.invalidateQueries({
            queryKey: ['call-graph', scanId],
          });
        }
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [scanId, queryClient]);

  if (isLoading || (!ready && !notFound)) {
    return <TestCasesSkeleton />;
  }
  if (notFound) return <ComponentNotFound />;

  return (
    <div className={`${testCasesPageClass} components-dashboard change-analysis-view`}>
      <OnboardingPageHeader
        eyebrow="Code Analysis"
        title="Test Cases"
        subtitle="Browse test script inventory, inspect folder structure, and review method coverage per test case."
      />

      <OnboardingProjectHeader
        org={component.orgName}
        project={component.projectName || '—'}
        repoName={component.repoName || '—'}
        branchName={formatBranch(component.branchName) || '—'}
      />

      <section className={testCasesCardClass} aria-label="Test cases">
        {!scanId ? (
          <div className={testCasesEmptyStateClass}>
            <p>
              No scan is selected for this component. Complete onboarding and
              run a code scan, then return here to browse test cases.
            </p>
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                navigate(
                  `/sia/components/code_analysis/inventory/${component.dbRepoId}`,
                )
              }>
              Go to Inventory
            </Button>
          </div>
        ) : !sessionReady ? (
          <div className={testCasesCardChildClass}>
            <TestCasesTabSkeleton />
          </div>
        ) : (
          <div className={testCasesCardChildClass}>
            <TestCasesTab
              scanId={scanId}
              codeRepositoryMongoId={component.dbRepoId}
              patToken={component.pat}
            />
          </div>
        )}
      </section>
    </div>
  );
};

export default TestCasesPage;
